<template>
  <div>
    <app-bar
      :title="eventDate.name || 'Aanmelden'"
      :show-back-button="true"
      :back-button-path="`/events/${dateId}/${securityHash}`"
    ></app-bar>
    <spinner-loader v-if="loading"></spinner-loader>
    <template v-else-if="Object.keys(eventDate).length">
      <div class="container pb-36">
        <!-- Event header -->
        <div class="flex items-center gap-3 pt-4 mb-6">
          <div
            class="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-gray-100"
          >
            <img
              :src="eventDate.imageUrl"
              class="w-full h-full object-cover"
              alt=""
            />
          </div>
          <div class="min-w-0">
            <h2
              class="text-gray-900 font-semibold text-lg leading-tight truncate"
            >
              {{ eventDate.name }}
            </h2>
            <p v-if="locationName" class="text-gray-400 text-sm">
              {{ locationName }}
            </p>
          </div>
        </div>

        <!-- Primary date label -->
        <p class="text-gray-400 text-xs uppercase tracking-wide mb-2">
          {{
            primaryDateIsFull &&
            !['yes', 'holding'].includes(attendanceOfThisEventDate)
              ? 'DEZE DATUM IS VOL'
              : 'JE MELDT JE AAN VOOR DEZE DATUM'
          }}
        </p>

        <!-- Primary date card: full, user NOT registered (waitlist state) -->
        <div
          v-if="
            primaryDateIsFull &&
            !['yes', 'holding'].includes(attendanceOfThisEventDate) &&
            primaryDateInFuture
          "
          class="rounded-2xl border-2 border-red-100 bg-white overflow-hidden mb-6"
        >
          <div class="flex items-center gap-4 p-4">
            <div
              class="flex-shrink-0 w-14 h-14 rounded-xl bg-red-50 text-red-400 flex flex-col items-center justify-center"
            >
              <span class="text-xl leading-tight font-medium">
                {{ primaryDayNum }}
              </span>
              <span class="text-xs leading-tight">{{ primaryMonthShort }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                <div class="flex items-center gap-1 text-gray-600 text-sm">
                  <icon-base size="14" class="text-gray-400">
                    <icon-clock />
                  </icon-base>
                  <span>{{ primaryStartTime }}-{{ primaryEndTime }}</span>
                </div>
                <div
                  v-if="locationName"
                  class="flex items-center gap-1 text-gray-600 text-sm min-w-0"
                >
                  <icon-base size="14" class="flex-shrink-0 text-gray-400">
                    <icon-pin />
                  </icon-base>
                  <span class="truncate">{{ locationName }}</span>
                </div>
              </div>
              <span class="text-red-500 text-xs">Vol</span>
            </div>
          </div>
          <div v-if="waitlistEnabled" class="px-4 pb-4 pt-0">
            <button
              v-if="isOnWaitlist(dateId)"
              class="w-full py-2.5 rounded-xl border-2 border-yellow-400 bg-white text-yellow-600 text-sm flex items-center justify-center gap-2 hover:bg-yellow-50 transition-colors"
              @click="openWaitlistModal(dateId, 'leave', primaryDateLabel)"
            >
              <icon-base size="14"><icon-bell /></icon-base>
              Op wachtlijst &#10003;
            </button>
            <button
              v-else
              class="w-full py-2.5 rounded-xl bg-yellow-500 text-white text-sm flex items-center justify-center gap-2 shadow-sm hover:bg-yellow-600 transition-colors"
              @click="openWaitlistModal(dateId, 'join', primaryDateLabel)"
            >
              <icon-base size="14"><icon-bell /></icon-base>
              Plaats me op de wachtlijst
            </button>
          </div>
        </div>

        <!-- Primary date card: toggle (not full, OR full but already registered) -->
        <button
          v-else
          class="w-full text-left flex items-center gap-4 p-4 rounded-2xl border-2 mb-6 transition-colors bg-white shadow-sm"
          :class="[
            ['yes', 'holding'].includes(changedDateAttendance)
              ? 'border-green-600'
              : 'border-gray-200',
            { 'cursor-not-allowed': primaryDateLocked }
          ]"
          :disabled="primaryDateLocked"
          @click="togglePrimaryDateAttendance"
        >
          <div
            class="flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center transition-colors"
            :class="
              ['yes', 'holding'].includes(changedDateAttendance)
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-400'
            "
          >
            <span class="text-xl leading-tight font-medium">
              {{ primaryDayNum }}
            </span>
            <span class="text-xs leading-tight">{{ primaryMonthShort }}</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
              <div class="flex items-center gap-1 text-gray-700 text-sm">
                <icon-base
                  size="14"
                  :class="
                    ['yes', 'holding'].includes(changedDateAttendance)
                      ? 'text-green-600'
                      : 'text-gray-300'
                  "
                >
                  <icon-clock />
                </icon-base>
                <span>{{ primaryStartTime }}-{{ primaryEndTime }}</span>
              </div>
              <div
                v-if="locationName"
                class="flex items-center gap-1 text-gray-700 text-sm min-w-0"
              >
                <icon-base
                  size="14"
                  class="flex-shrink-0"
                  :class="
                    ['yes', 'holding'].includes(changedDateAttendance)
                      ? 'text-green-600'
                      : 'text-gray-300'
                  "
                >
                  <icon-pin />
                </icon-base>
                <span class="truncate">{{ locationName }}</span>
              </div>
            </div>
            <span
              v-if="
                ['yes', 'holding'].includes(attendanceOfThisEventDate) &&
                ['yes', 'holding'].includes(changedDateAttendance)
              "
              class="text-green-600 text-xs font-medium"
            >
              Aangemeld
            </span>
            <span
              v-if="primaryDateLocked"
              class="primary-date-locked block text-gray-400 text-xs"
            >
              {{ $t('event.attendanceLocked.toggle') }}
            </span>
            <span
              v-else-if="primarySpotsRemaining !== null"
              class="text-xs"
              :class="
                primarySpotsRemaining <= 3
                  ? 'text-yellow-600'
                  : 'text-green-600'
              "
            >
              {{ primarySpotsRemaining }}
              {{ primarySpotsRemaining === 1 ? 'plek' : 'plekken' }} over
            </span>
          </div>
          <!-- Toggle switch -->
          <div
            v-if="!primaryDateLocked"
            class="relative inline-flex flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
            :class="
              ['yes', 'holding'].includes(changedDateAttendance)
                ? 'bg-green-600'
                : 'bg-gray-200'
            "
            style="width: 48px; height: 28px"
            @click.stop="togglePrimaryDateAttendance"
          >
            <span
              class="pointer-events-none inline-block rounded-full bg-white shadow-md transform transition-transform duration-200 ease-in-out"
              :class="
                ['yes', 'holding'].includes(changedDateAttendance)
                  ? 'translate-x-6'
                  : 'translate-x-0.5'
              "
              style="width: 22px; height: 22px; margin-top: 3px"
            ></span>
          </div>
        </button>

        <!-- Other dates section -->
        <template v-if="otherDates.length">
          <div class="flex items-center gap-2 mb-2.5">
            <icon-base size="16" class="text-gray-400">
              <icon-calendar />
            </icon-base>
            <p class="text-gray-400 text-xs uppercase tracking-wide">
              OOK MEEDOEN OP ANDERE DATA?
            </p>
          </div>
          <div
            class="bg-white rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-50 mb-6"
          >
            <template v-for="(date, index) in otherDates">
              <!-- Full date row -->
              <div
                v-if="
                  date.isFull &&
                  !['yes', 'holding'].includes(date.currentAttendance)
                "
                :key="`full-${index}`"
                class="flex items-center gap-3.5 px-4 py-3"
              >
                <div
                  class="flex-shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center bg-red-50 text-red-400"
                >
                  <span class="text-base leading-tight font-medium">
                    {{ formatDayNum(date.startDateTime) }}
                  </span>
                  <span class="leading-tight" style="font-size: 0.6rem">
                    {{ formatMonthShort(date.startDateTime) }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="text-gray-600 text-sm">
                      {{ formatDateRow(date.startDateTime) }}
                    </span>
                    <span class="text-gray-400 text-xs">
                      {{
                        formatTimeRange(date.startDateTime, date.endDateTime)
                      }}
                    </span>
                  </div>
                  <span class="text-red-400 text-xs">Vol</span>
                </div>
                <template v-if="waitlistEnabled">
                  <button
                    v-if="isOnWaitlist(date.id)"
                    class="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 border-yellow-400 bg-white text-yellow-600 text-xs whitespace-nowrap hover:bg-yellow-50 transition-colors"
                    @click="
                      openWaitlistModal(
                        date.id,
                        'leave',
                        formatDateRow(date.startDateTime)
                      )
                    "
                  >
                    <icon-base size="12"><icon-bell /></icon-base>
                    Wachtlijst &#10003;
                  </button>
                  <button
                    v-else
                    class="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-yellow-500 text-white text-xs whitespace-nowrap hover:bg-yellow-600 transition-colors shadow-sm"
                    @click="
                      openWaitlistModal(
                        date.id,
                        'join',
                        formatDateRow(date.startDateTime)
                      )
                    "
                  >
                    <icon-base size="12"><icon-bell /></icon-base>
                    Wachtlijst
                  </button>
                </template>
              </div>

              <!-- Available date row (toggle) -->
              <button
                v-else
                :key="`avail-${index}`"
                class="w-full flex items-center gap-3.5 px-4 py-3 text-left transition-colors"
                :class="isDateChecked(date) ? 'bg-green-50' : ''"
                @click="toggleDateAttendance(date)"
              >
                <div
                  class="flex-shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center transition-colors"
                  :class="
                    isDateChecked(date)
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  "
                >
                  <span class="text-base leading-tight font-medium">
                    {{ formatDayNum(date.startDateTime) }}
                  </span>
                  <span class="leading-tight" style="font-size: 0.6rem">
                    {{ formatMonthShort(date.startDateTime) }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="text-gray-600 text-sm">
                      {{ formatDateRow(date.startDateTime) }}
                    </span>
                    <span class="text-gray-400 text-xs">
                      {{
                        formatTimeRange(date.startDateTime, date.endDateTime)
                      }}
                    </span>
                  </div>
                  <span
                    v-if="
                      ['yes', 'holding'].includes(date.currentAttendance) &&
                      isDateChecked(date)
                    "
                    class="text-green-600 text-xs"
                  >
                    Aangemeld
                  </span>
                  <span
                    v-else-if="dateSpotsRemaining(date) !== null"
                    class="text-xs"
                    :class="
                      dateSpotsRemaining(date) <= 3
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    "
                  >
                    {{ dateSpotsRemaining(date) }}
                    {{ dateSpotsRemaining(date) === 1 ? 'plek' : 'plekken' }}
                    over
                  </span>
                </div>
                <!-- Toggle switch -->
                <div
                  class="relative inline-flex flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out"
                  :class="isDateChecked(date) ? 'bg-green-600' : 'bg-gray-200'"
                  style="width: 48px; height: 28px"
                >
                  <span
                    class="pointer-events-none inline-block rounded-full bg-white shadow-md transform transition-transform duration-200 ease-in-out"
                    :class="
                      isDateChecked(date) ? 'translate-x-6' : 'translate-x-0.5'
                    "
                    style="width: 22px; height: 22px; margin-top: 3px"
                  ></span>
                </div>
              </button>
            </template>
          </div>
        </template>

        <!-- Summary section -->
        <div class="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
          <h4 class="text-gray-700 font-medium mb-3">Overzicht</h4>
          <div class="flex flex-col gap-2">
            <template
              v-if="
                selectedDatesForSummary.length ||
                removedDates.length ||
                waitlistDatesForSummary.length
              "
            >
              <!-- Registered dates -->
              <div
                v-if="selectedDatesForSummary.length"
                class="flex items-start justify-between"
              >
                <span class="text-gray-500 text-sm">
                  Aangemeld ({{
                    selectedDatesForSummary.length === 1
                      ? '1 datum'
                      : `${selectedDatesForSummary.length} data`
                  }})
                </span>
                <div class="flex flex-col items-end gap-0.5">
                  <span
                    v-for="(d, i) in selectedDatesForSummary"
                    :key="i"
                    class="text-gray-700 text-sm flex items-center gap-1.5"
                  >
                    <span
                      v-if="newDateIds.includes(d.id || dateId)"
                      class="text-green-600 bg-green-50 px-1.5 py-0.5 rounded text-xs"
                    >
                      nieuw
                    </span>
                    {{ formatDateSummary(d.startDateTime) }}
                  </span>
                </div>
              </div>

              <!-- Removed dates -->
              <div
                v-if="removedDates.length"
                class="flex items-start justify-between"
              >
                <span class="text-red-400 text-sm">Afgemeld</span>
                <div class="flex flex-col items-end gap-0.5">
                  <span
                    v-for="(d, i) in removedDates"
                    :key="i"
                    class="text-red-400 text-sm line-through"
                  >
                    {{ formatDateSummary(d.startDateTime) }}
                  </span>
                </div>
              </div>

              <!-- Waitlist dates -->
              <div
                v-if="waitlistDatesForSummary.length"
                class="flex items-start justify-between"
              >
                <span class="text-yellow-600 text-sm flex items-center gap-1">
                  <icon-base size="12"><icon-bell /></icon-base>
                  Wachtlijst
                </span>
                <div class="flex flex-col items-end gap-0.5">
                  <span
                    v-for="(d, i) in waitlistDatesForSummary"
                    :key="i"
                    class="text-yellow-600 text-sm"
                  >
                    {{ formatDateSummary(d.startDateTime) }}
                  </span>
                </div>
              </div>

              <!-- Time and location (when any registered dates) -->
              <template v-if="selectedDatesForSummary.length">
                <div class="flex items-center justify-between">
                  <span class="text-gray-500 text-sm">Tijd</span>
                  <span class="text-gray-700 text-sm">
                    {{ primaryStartTime }}-{{ primaryEndTime }}
                  </span>
                </div>
                <div
                  v-if="locationName"
                  class="flex items-center justify-between"
                >
                  <span class="text-gray-500 text-sm">Locatie</span>
                  <span class="text-gray-700 text-sm text-right">
                    {{ locationName }}
                  </span>
                </div>
                <div
                  v-if="isUnlimited"
                  class="flex items-center justify-between pt-2 mt-1 border-t border-gray-100"
                >
                  <span class="text-gray-500 text-sm">Betaling</span>
                  <span class="text-gray-900 text-sm font-medium">
                    Seizoenskaart
                  </span>
                </div>
                <div
                  v-else-if="!ownedCreditcardForThisEvent && amountToPay !== 0"
                  class="flex items-center justify-between pt-2 mt-1 border-t border-gray-100"
                >
                  <span class="text-gray-500 text-sm">
                    {{ amountToPay >= 0 ? 'Te betalen' : 'Te ontvangen' }}
                  </span>
                  <span class="text-gray-900 text-sm font-medium">
                    {{ $n(Math.abs(amountToPay), 'currency') }}
                  </span>
                </div>
                <div
                  v-else-if="ownedCreditcardForThisEvent && creditsToPay !== 0"
                  class="flex items-center justify-between pt-2 mt-1 border-t border-gray-100"
                >
                  <span class="text-gray-500 text-sm">
                    {{
                      creditsToPay >= 0 ? 'Aantal strippen' : 'Strippen retour'
                    }}
                  </span>
                  <span class="text-gray-900 text-sm font-medium">
                    {{ Math.abs(creditsToPay) }}
                  </span>
                </div>
              </template>
            </template>
            <p v-else class="text-gray-400 text-sm">
              Zet een datum aan om je aan te melden
            </p>
          </div>
        </div>

        <!-- Creditcard section (hidden for the organizer, who joins for free) -->
        <template v-if="activeCard && !currentUserIsOwner">
          <div class="text-sm">
            <template v-if="ownedCreditcardForThisEvent">
              <div class="text-sm my-6">
                Je hebt een
                {{ isSeasonCard ? 'seizoenskaart' : 'strippenkaart' }}
                voor deze activiteit
                <template v-if="isUnlimited">
                  met
                  <strong>onbeperkt gebruik</strong>
                  .
                </template>
                <template v-else>
                  en hebt nog
                  <strong>
                    {{ ownedCreditcardForThisEvent.remaining }} strippen
                  </strong>
                  over.
                </template>
              </div>
            </template>
            <template v-else>
              <h2>Strippenkaart</h2>
              <div v-if="eventDate.creditcardOnly">
                Voor dit event moet je een strippenkaart aanschaffen om mee te
                kunnen doen. Je hoeft dan niet elke keer opnieuw een iDeal
                betaling te doen. Handig!
              </div>
              <div v-else>
                Door een strippenkaart aan te schaffen hoef je niet elke keer
                opnieuw een iDeal betaling te doen maar reken je gewoon per
                strip af. Handig!
              </div>
              <div class="mt-4 text-sm border border-gray-300 p-4 rounded-md">
                <p class="text-base mb-4">{{ activeCard.name }}</p>
                <div class="flex">
                  <div class="flex-1 font-semibold">
                    Prijs van de strippenkaart
                  </div>
                  <div>
                    {{ $n(activeCard.cost, 'currency') }}
                  </div>
                </div>
                <div class="flex mt-2">
                  <div class="flex-1 font-semibold">
                    {{
                      activeCard.type === 'season_card'
                        ? 'Geldigheid'
                        : 'Aantal keer te gebruiken'
                    }}
                  </div>
                  <div>
                    {{
                      activeCard.type === 'season_card'
                        ? 'Onbeperkt'
                        : activeCard.times_to_use + ' keer'
                    }}
                  </div>
                </div>
              </div>
              <button
                class="button button-primary w-full mt-6"
                @click="goToPayment(PURCHASE_CREDITCARD)"
              >
                Koop strippenkaart
              </button>
            </template>
          </div>
        </template>
      </div>

      <!-- Bottom bar -->
      <div class="border-t border-gray-100 bottom-0 fixed w-full bg-white">
        <div class="max-w-3xl mx-auto px-5 pt-3 pb-4">
          <template
            v-if="
              eventDate.creditcardOnly &&
              !ownedCreditcardForThisEvent &&
              !currentUserIsOwner
            "
          >
            <p class="text-center text-gray-500 text-sm mb-2">
              Koop een strippenkaart om mee te kunnen doen
            </p>
            <button
              class="button button-primary w-full h-auto py-4 rounded-full"
              @click="goToPayment(PURCHASE_CREDITCARD)"
            >
              Koop strippenkaart
            </button>
          </template>
          <template v-else-if="changedDates.length">
            <p
              v-if="isUnlimited"
              class="text-center text-gray-500 text-sm mb-2"
            >
              Betaling:
              <span class="text-gray-900 font-semibold">Seizoenskaart</span>
            </p>
            <template v-else>
              <p
                v-if="
                  (ownedCreditcardForThisEvent || creditsToReturn) &&
                  creditsToPay !== 0
                "
                class="text-center text-gray-500 text-sm mb-2"
              >
                {{
                  creditsToPay >= 0 ? 'Aantal strippen:' : 'Strippen retour:'
                }}
                <span class="text-gray-900 font-semibold">
                  {{ Math.abs(creditsToPay) }}
                </span>
              </p>
              <p
                v-if="amountToPay !== 0 && !creditsCoverNewSignups"
                class="text-center text-gray-500 text-sm mb-2"
              >
                {{ amountToPay >= 0 ? 'Te betalen:' : 'Te ontvangen:' }}
                <span class="text-gray-900 font-semibold">
                  {{ $n(Math.abs(amountToPay), 'currency') }}
                </span>
              </p>
            </template>
            <button
              class="button button-primary w-full h-auto py-4 rounded-full"
              @click="saveAttendance"
            >
              {{
                amountToPay > 0 && !ownedCreditcardForThisEvent
                  ? 'Betalen'
                  : 'Opslaan'
              }}
            </button>
          </template>
          <template v-else>
            <p class="text-center text-gray-400 text-sm py-1">
              <template v-if="selectedDatesForSummary.length">
                Geen wijzigingen
              </template>
              <template v-else-if="primaryDateIsFull && waitlistEnabled">
                Deze datum is vol. Zet jezelf op de wachtlijst of kies een
                andere datum.
              </template>
              <template v-else-if="primaryDateIsFull">
                Deze datum is vol.
              </template>
              <template v-else>Zet een datum aan om je aan te melden</template>
            </p>
          </template>
        </div>
      </div>
    </template>

    <div v-else class="container pb-28">
      <h1>Oeps, er gaat iets fout!</h1>
    </div>

    <modal
      :show="showConfirmModal"
      :show-close-icon="true"
      @close="
        () => {
          showConfirmModal = false
          $router.push(`/events/${dateId}/${securityHash}`)
        }
      "
    >
      <template v-slot:header>Gelukt!</template>
      <p>Je hebt je aanwezigheid aangepast voor deze activiteit!</p>
    </modal>

    <modal
      :show="showErrorModal"
      :show-close-icon="true"
      @close="
        () => {
          showErrorModal = false
          $router.push(`/events/${dateId}/${securityHash}`)
        }
      "
    >
      <template v-slot:header>Oeps!</template>
      <p v-if="attendanceLocked" class="attendance-locked-error">
        {{ $t('event.attendanceLocked.error') }}
      </p>
      <p v-else>
        Er is iets fout gegaan... Ga terug naar het event en probeer het opnieuw
      </p>
    </modal>

    <spinner-loader v-if="paymentStatusLoading" />
    <modal
      v-if="!paymentStatusLoading"
      :show="showPaymentFeedbackModal"
      :show-close-icon="true"
      @close="
        () => {
          $router.push($route.path)
        }
      "
    >
      <template v-if="paymentSuccess" v-slot:header>Gelukt!</template>
      <template v-else-if="paymentPending" v-slot:header>
        In behandeling
      </template>
      <template v-else v-slot:header>Oeps...</template>
      <p v-if="paymentSuccess">
        Je betaling is ontvangen! Meld je nu makkelijk en snel aan.
      </p>
      <p v-else-if="paymentPending">
        Je betaling wordt nog verwerkt. Dit kan enkele minuten duren. Je
        ontvangt een bevestiging per e-mail zodra de betaling is afgerond.
      </p>
      <p v-else-if="paymentFailed">
        Je betaling is helaas niet gelukt. Probeer het opnieuw of neem contact
        met ons op via contact@inviplay.nl.
      </p>
      <p v-else>
        We konden de status van je betaling niet ophalen. Controleer je e-mail
        voor een bevestiging of neem contact met ons op via contact@inviplay.nl.
      </p>
    </modal>

    <modal
      :show="showModalCreditsDone"
      :show-close-icon="true"
      @close="showModalCreditsDone = false"
    >
      <template v-slot:header>Niet genoeg strippen</template>
      <p>
        Je hebt niet genoeg strippen om je voor {{ creditsToPay }} activiteiten
        aan te melden. Maak nu eerst deze strippenkaart op en koop vervolgens
        een nieuwe strippenkaart als je je voor meer activiteiten wil aanmelden.
      </p>
    </modal>

    <!-- Waitlist modal -->
    <modal
      v-if="waitlistEnabled"
      :show="showWaitlistModal"
      :show-close-icon="true"
      @close="showWaitlistModal = false"
    >
      <template v-slot:header>
        {{
          waitlistModalType === 'join'
            ? 'Op wachtlijst zetten?'
            : 'Afmelden van wachtlijst?'
        }}
      </template>
      <p>
        {{
          waitlistModalType === 'join'
            ? `Je ontvangt een melding zodra er een plek vrijkomt${
                waitlistModalLabel ? ' voor ' + waitlistModalLabel : ''
              }.`
            : `Je wordt van de wachtlijst verwijderd${
                waitlistModalLabel ? ' voor ' + waitlistModalLabel : ''
              } en ontvangt geen meldingen meer.`
        }}
      </p>
      <div class="flex flex-col gap-2 mt-4">
        <button
          class="button button-primary w-full h-auto py-3 rounded-full"
          @click="confirmWaitlist"
        >
          {{
            waitlistModalType === 'join'
              ? 'Ja, zet mij op de wachtlijst'
              : 'Ja, meld mij af'
          }}
        </button>
        <button
          class="button button-secondary w-full h-auto py-3 rounded-full"
          @click="showWaitlistModal = false"
        >
          Annuleren
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import AppBar from '@/components/AppBar'
import SpinnerLoader from '@/components/SpinnerLoader'
import { mapState, mapGetters } from 'vuex'
import {
  EVENT_DATES_GET,
  EVENT_JOIN_WAITLIST,
  EVENT_LEAVE_WAITLIST
} from '@/store/actions/date'
import {
  EVENT_LOGGED_IN_USER_ATTENDANCE,
  EVENT_STORE_DATES_TO_UPDATE
} from '@/store/actions/event'
import IconBase from '@/components/IconBase'
import IconClock from '@/components/icons/IconClock'
import IconPin from '@/components/icons/IconPin'
import IconCalendar from '@/components/icons/IconCalendar'
import IconBell from '@/components/icons/IconBell'
import { PARTICIPANT_GET_CREDITCARD_ON_EVENT } from '@/store/actions/participant'
import { EVENT_GET_CREDITCARDS } from '@/store/actions/event'
import {
  PURCHASE_CREDITCARD,
  SINGLE_PAYMENT,
  CREDIT_PAYMENT,
  REFUND
} from '@/utils/constants'
import {
  PAYMENT_SET_TYPE,
  PAYMENT_GET_STATUS_BY_ID
} from '@/store/actions/payment'
import { EVENT_UPDATE_ATTENDANCE } from '@/store/actions/event'
import Modal from '../../components/Modal.vue'
import { OPTION_GET } from '../../store/actions/option'
import { CREDITCARD_GET_GROUP } from '../../store/actions/creditcard'
import analytics from '@/utils/analytics'
import config from '@/utils/config'
import {
  ATTENDANCE_LOCKED_EVENT_STARTED,
  hasDateStarted
} from '@/utils/attendance'

export default {
  name: 'EventSignup',
  components: {
    AppBar,
    SpinnerLoader,
    IconBase,
    IconClock,
    IconPin,
    IconCalendar,
    IconBell,
    Modal
  },
  data() {
    return {
      dateId: parseInt(this.$route.params.dateId),
      securityHash: this.$route.params.securityHash,
      datesToUpdate: [],
      PURCHASE_CREDITCARD: PURCHASE_CREDITCARD,
      SINGLE_PAYMENT: SINGLE_PAYMENT,
      CREDIT_PAYMENT: CREDIT_PAYMENT,
      showConfirmModal: false,
      showErrorModal: false,
      attendanceLocked: false,
      showPaymentFeedbackModal: false,
      paymentId: this.$route.query.pid,
      paymentStatusLoading: false,
      numberOfRepeats: 0,
      paymentStatusTimeout: null,
      paymentOverallTimeout: null,
      showModalCreditsDone: false,
      // Waitlist state
      waitlistDateIds: [],
      showWaitlistModal: false,
      waitlistModalType: 'join',
      waitlistModalDateId: null,
      waitlistModalLabel: ''
    }
  },
  computed: {
    ...mapState({
      eventDate: (state) => state.date.eventDate,
      currentAttendance: (state) => state.event.attendance,
      participantCreditcards: (state) => state.participant.creditcards,
      creditcards: (state) => state.event.creditcards,
      refundCosts: (state) => state.option.options.payments_refund_fee,
      paymentStatus: (state) => state.payment.status,
      loading: (state) =>
        state.event.status === 'loading' || state.date.status === 'loading'
    }),
    ...mapGetters(['authId']),
    // The organizer joins/leaves their own event for free — mirror the backend,
    // which sets participantNeedsToPay to false when ownerId === participantId.
    currentUserIsOwner: function () {
      return (
        !!this.eventDate && this.eventDate.ownerId === parseInt(this.authId)
      )
    },
    action: function () {
      return this.$route.query.action
    },
    paymentSuccess: function () {
      if (!this.paymentId || !this.paymentStatus) return false
      const status = this.paymentStatus[this.paymentId]
      return status === 'paid' || status === 'completed'
    },
    paymentPending: function () {
      if (!this.paymentId || !this.paymentStatus) return false
      const status = this.paymentStatus[this.paymentId]
      return status === 'pending' || status === 'open' || status === 'created'
    },
    paymentFailed: function () {
      if (!this.paymentId || !this.paymentStatus) return false
      const status = this.paymentStatus[this.paymentId]
      return (
        status === 'expired' ||
        status === 'failed' ||
        status === 'canceled' ||
        status === 'cancelled'
      )
    },
    locationName: function () {
      return this.eventDate.location?.name || ''
    },
    primaryDayNum: function () {
      return DateTime.fromISO(this.eventDate.startDateTime).day
    },
    primaryMonthShort: function () {
      return DateTime.fromISO(this.eventDate.startDateTime)
        .setLocale('nl')
        .toFormat('LLL')
        .toLowerCase()
    },
    primaryStartTime: function () {
      return this.eventDate.startDateTimeFormatted || ''
    },
    primaryEndTime: function () {
      return this.eventDate.endDateTimeFormatted || ''
    },
    primaryDateLabel: function () {
      return DateTime.fromISO(this.eventDate.startDateTime)
        .setLocale('nl')
        .toFormat('EEE d LLL')
    },
    primarySpotsRemaining: function () {
      if (
        this.eventDate.maximumParticipants != null &&
        this.eventDate.numberOfParticipants != null
      ) {
        return Math.max(
          0,
          this.eventDate.maximumParticipants -
            this.eventDate.numberOfParticipants
        )
      }
      return null
    },
    // Derive fullness from participant counts when available — more reliable
    // than the server's pre-computed isFull flag, which can be stale after
    // a deregistration (numberOfParticipants updates correctly).
    primaryDateIsFull: function () {
      if (
        this.eventDate.maximumParticipants != null &&
        this.eventDate.numberOfParticipants != null
      ) {
        return (
          this.eventDate.numberOfParticipants >=
          this.eventDate.maximumParticipants
        )
      }
      return this.eventDate.isFull === true
    },
    primaryDateInFuture: function () {
      return (
        !!this.eventDate?.startDateTime &&
        DateTime.fromISO(this.eventDate.startDateTime) > DateTime.local()
      )
    },
    // Signed up for a date that has started: signing out is no longer allowed
    // (the API answers 422 ATTENDANCE_LOCKED_EVENT_STARTED); signing up still is.
    primaryDateLocked: function () {
      return (
        hasDateStarted(this.eventDate.startDateTime) &&
        ['yes', 'holding'].includes(this.attendanceOfThisEventDate)
      )
    },
    waitlistEnabled: function () {
      return (
        config.featureFlags.waitlist && this.eventDate.waitlistEnabled === true
      )
    },
    otherDates: function () {
      return (
        this.eventDate?.allDates
          ?.filter(
            (date) =>
              DateTime.fromISO(date.startDateTime) > DateTime.local() &&
              date.id !== this.eventDate.id
          )
          .map((od) => ({
            ...od,
            currentAttendance: this.currentAttendance.find(
              (ca) => ca.dateId === od.id
            )?.attendance
          })) || []
      )
    },
    selectedDatesForSummary: function () {
      const primary = ['yes', 'holding'].includes(this.changedDateAttendance)
        ? [this.eventDate]
        : []
      const extras = this.otherDates.filter((d) =>
        ['yes', 'holding'].includes(this.setValue(d))
      )
      return [...primary, ...extras]
    },
    // Only send the fields the server needs — prevents accidentally sending
    // extra properties (e.g. isFull, maximumParticipants) from attendance records.
    changedDatesPayload: function () {
      return this.changedDates.map(
        ({ dateId, attendance, payment_status }) => ({
          dateId,
          attendance,
          payment_status
        })
      )
    },
    // IDs of dates being newly added (not previously registered)
    newDateIds: function () {
      return this.changedDates
        .filter((d) => d.attendance === 'yes')
        .map((d) => d.dateId)
    },
    // Date objects that are being removed (previously registered, now unchecked)
    removedDates: function () {
      return this.changedDates
        .filter(
          (d) =>
            d.attendance === 'no' &&
            this.currentAttendance.some(
              (c) =>
                c.dateId === d.dateId &&
                ['yes', 'holding'].includes(c.attendance)
            )
        )
        .map((d) => {
          if (d.dateId === this.dateId) return this.eventDate
          return this.otherDates.find((od) => od.id === d.dateId)
        })
        .filter(Boolean)
    },
    // Date objects user is on the waitlist for (summary display)
    waitlistDatesForSummary: function () {
      if (!this.waitlistEnabled) return []
      return this.waitlistDateIds
        .map((id) => {
          if (id === this.dateId) return this.eventDate
          return (
            this.otherDates.find((od) => od.id === id) ||
            this.eventDate?.allDates?.find((d) => d.id === id)
          )
        })
        .filter(Boolean)
        .filter((d) => DateTime.fromISO(d.startDateTime) > DateTime.local())
    },
    activeCard: function () {
      return this.eventDate &&
        this.creditcards[this.eventDate.eventId] &&
        (parseFloat(this.eventDate.costFeeIncluded) ||
          this.eventDate.creditcardOnly)
        ? this.creditcards[this.eventDate.eventId][0]
        : null
    },
    ownedCreditcardForThisEvent: function () {
      return (
        this.eventDate && this.participantCreditcards[this.eventDate.eventId]
      )
    },
    isSeasonCard: function () {
      return this.ownedCreditcardForThisEvent?.type === 'season_card'
    },
    isUnlimited: function () {
      return (
        this.isSeasonCard ||
        this.ownedCreditcardForThisEvent?.remaining === null
      )
    },
    timesToPay: function () {
      return this.changedDates.length
        ? this.changedDates.reduce((number, date) => {
            if (date.attendance === 'yes' && date.payment_status !== 'paid') {
              return number + 1
            } else if (
              date.attendance === 'no' &&
              date.payment_status === 'paid'
            ) {
              return number - 1
            }
            return number
          }, 0)
        : 0
    },
    timesToPayAbs: function () {
      return Math.abs(this.timesToPay)
    },
    amountToPay: function () {
      // The organizer never pays for their own event.
      if (this.currentUserIsOwner) return 0
      return this.timesToPay > 0
        ? this.timesToPay * parseFloat(this.eventDate.costFeeIncluded)
        : this.timesToPay < 0
        ? this.timesToPay *
          (parseFloat(this.eventDate.costFeeIncluded) - this.refundCosts)
        : 0
    },
    creditsToPay: function () {
      // The organizer joins for free, so no strippenkaart is ever spent.
      if (this.currentUserIsOwner) return 0
      return this.changedDates.length
        ? this.changedDates.reduce((credits, date) => {
            if (date.attendance === 'yes' && date.payment_status !== 'credit') {
              return credits + 1
            } else if (
              date.attendance === 'no' &&
              date.payment_status === 'credit'
            ) {
              return credits - 1
            }
            return credits
          }, 0)
        : 0
    },
    creditsToPayAbs: function () {
      return Math.abs(this.creditsToPay)
    },
    creditsToReturn: function () {
      return this.creditsToPay < 0
    },
    // True when new signups will be fully covered by the user's strippenkaart,
    // so no real money is charged — used to hide the "Te betalen" label.
    creditsCoverNewSignups: function () {
      if (this.timesToPay <= 0 || !this.ownedCreditcardForThisEvent)
        return false
      if (this.isUnlimited) return true
      const remaining =
        parseInt(this.ownedCreditcardForThisEvent.remaining) || 0
      return remaining >= this.timesToPay
    },
    changedDates: function () {
      return this.datesToUpdate.reduce((list, dateToUpdate) => {
        const current = this.currentAttendance.find(
          (c) => c.dateId === dateToUpdate.dateId
        )
        // For never-registered dates, only 'yes' is a meaningful change;
        // setting 'no' on a date you were never signed up for is a no-op.
        const hasActualChange = current
          ? current.attendance !== dateToUpdate.attendance &&
            !(
              dateToUpdate.attendance === 'no' &&
              !['yes', 'holding'].includes(current.attendance)
            )
          : dateToUpdate.attendance === 'yes'
        return hasActualChange && !dateToUpdate.isFull
          ? [...list, dateToUpdate]
          : list
      }, [])
    },
    userIsParticipant: function () {
      return (
        this.currentAttendance.findIndex((c) => c.dateId === this.dateId) >= 0
      )
    },
    changedDateAttendance: function () {
      const currentDate = this.datesToUpdate.find(
        (cd) => cd.dateId === this.dateId
      )
      return currentDate || this.primaryDateIsFull
        ? currentDate?.attendance || null
        : 'yes'
    },
    attendanceOfThisEventDate: function () {
      return this.currentAttendance.find((c) => c.dateId === this.dateId)
        ?.attendance
    }
  },
  watch: {
    action(action) {
      if (action === 'payment' && this.paymentId) {
        this.startPaymentPolling()
      } else {
        this.showPaymentFeedbackModal = action === 'payment'
      }
    },
    paymentStatus(paymentStatus) {
      if (!this.paymentId) return
      const status = paymentStatus[this.paymentId]
      if (status === 'open' || status === 'pending' || status === 'created') {
        this.retryPaymentStatus()
      } else {
        this.stopPaymentPolling()
        this.paymentStatusLoading = false
        this.showPaymentFeedbackModal = true
      }
    },
    eventDate(event) {
      // Initialize primary date waitlist state from API data
      if (event.isOnWaitlist) {
        if (!this.waitlistDateIds.includes(this.dateId)) {
          this.waitlistDateIds = [...this.waitlistDateIds, this.dateId]
        }
      } else {
        this.waitlistDateIds = this.waitlistDateIds.filter(
          (id) => id !== this.dateId
        )
      }
      this.$store.dispatch(EVENT_LOGGED_IN_USER_ATTENDANCE, {
        eventId: event.eventId
      })
      this.$store.dispatch(PARTICIPANT_GET_CREDITCARD_ON_EVENT, {
        eventId: event.eventId
      })
      this.$store.dispatch(EVENT_GET_CREDITCARDS, this.eventDate.eventId)
      if (this.eventDate.groupId) {
        this.$store.dispatch(CREDITCARD_GET_GROUP, {
          groupId: this.eventDate.groupId
        })
      }
    },
    currentAttendance(attendance) {
      // Initialize waitlist state for other dates from attendance data
      const waitlistedIds = attendance
        .filter(
          (a) => a.attendance === 'waitlisted' && a.dateId !== this.dateId
        )
        .map((a) => a.dateId)
      const primaryWaitlistIds = this.eventDate.isOnWaitlist
        ? [this.dateId]
        : []
      this.waitlistDateIds = [...primaryWaitlistIds, ...waitlistedIds]

      const current = attendance.find((a) => a.dateId === this.dateId)
      this.datesToUpdate = [
        ...(!current && !this.primaryDateIsFull
          ? [
              {
                dateId: this.dateId,
                attendance: 'yes',
                payment_status: null
              }
            ]
          : []),
        ...attendance.filter(
          (att) =>
            !this.datesToUpdate.map((date) => date.dateId).includes(att.dateId)
        )
      ]
    },
    changedDates(dates) {
      this.$store.commit(EVENT_STORE_DATES_TO_UPDATE, dates)
    }
  },
  created: function () {
    if (this.action === 'payment' && this.paymentId) {
      this.startPaymentPolling()
    } else {
      this.showPaymentFeedbackModal = this.action === 'payment'
    }
    this.$store.dispatch(EVENT_DATES_GET, {
      dateId: this.dateId,
      securityHash: this.securityHash
    })
    this.$store.dispatch(OPTION_GET, 'payments_refund_fee')
  },
  beforeDestroy: function () {
    this.stopPaymentPolling()
  },
  methods: {
    startPaymentPolling: function () {
      this.paymentStatusLoading = true
      this.paymentOverallTimeout = setTimeout(() => {
        this.stopPaymentPolling()
        this.paymentStatusLoading = false
        this.showPaymentFeedbackModal = true
      }, 15000)
      this.$store.dispatch(PAYMENT_GET_STATUS_BY_ID, {
        paymentId: this.paymentId
      })
    },
    stopPaymentPolling: function () {
      if (this.paymentStatusTimeout) {
        clearTimeout(this.paymentStatusTimeout)
        this.paymentStatusTimeout = null
      }
      if (this.paymentOverallTimeout) {
        clearTimeout(this.paymentOverallTimeout)
        this.paymentOverallTimeout = null
      }
    },
    retryPaymentStatus: function () {
      if (this.numberOfRepeats < 10) {
        this.paymentStatusTimeout = setTimeout(() => {
          this.$store.dispatch(PAYMENT_GET_STATUS_BY_ID, {
            paymentId: this.paymentId
          })
          this.numberOfRepeats++
        }, 2000)
      } else {
        this.stopPaymentPolling()
        this.paymentStatusLoading = false
        this.showPaymentFeedbackModal = true
      }
    },
    formatDayNum(dateStr) {
      return DateTime.fromISO(dateStr).day
    },
    formatMonthShort(dateStr) {
      return DateTime.fromISO(dateStr)
        .setLocale('nl')
        .toFormat('LLL')
        .toLowerCase()
    },
    formatDateRow(dateStr) {
      return DateTime.fromISO(dateStr).setLocale('nl').toFormat('EEE d LLL')
    },
    formatTimeRange(startStr, endStr) {
      return `${DateTime.fromISO(startStr).toFormat(
        'HH:mm'
      )}-${DateTime.fromISO(endStr).toFormat('HH:mm')}`
    },
    formatDateSummary(dateStr) {
      return DateTime.fromISO(dateStr).setLocale('nl').toFormat('EEE d LLL')
    },
    dateSpotsRemaining(date) {
      if (
        date.maximumParticipants != null &&
        date.numberOfParticipants != null
      ) {
        return Math.max(0, date.maximumParticipants - date.numberOfParticipants)
      }
      return null
    },
    isDateChecked(date) {
      return ['yes', 'holding'].includes(this.setValue(date))
    },
    togglePrimaryDateAttendance() {
      if (this.primaryDateLocked) return
      this.setAttendance(
        this.dateId,
        ['yes', 'holding'].includes(this.changedDateAttendance) ? 'no' : 'yes'
      )
    },
    toggleDateAttendance(date) {
      this.setAttendance(date.id, this.isDateChecked(date) ? 'no' : 'yes')
    },
    isOnWaitlist(dateId) {
      return this.waitlistDateIds.includes(dateId)
    },
    openWaitlistModal(dateId, type, label) {
      this.waitlistModalDateId = dateId
      this.waitlistModalType = type
      this.waitlistModalLabel = label
      this.showWaitlistModal = true
    },
    async confirmWaitlist() {
      const action =
        this.waitlistModalType === 'join'
          ? EVENT_JOIN_WAITLIST
          : EVENT_LEAVE_WAITLIST
      try {
        await this.$store.dispatch(action, {
          eventId: this.eventDate.eventId,
          dateIds: [this.waitlistModalDateId],
          securityHash: this.securityHash
        })
        // Update local state optimistically; watchers will re-sync from store
        if (this.waitlistModalType === 'join') {
          if (!this.waitlistDateIds.includes(this.waitlistModalDateId)) {
            this.waitlistDateIds = [
              ...this.waitlistDateIds,
              this.waitlistModalDateId
            ]
          }
        } else {
          this.waitlistDateIds = this.waitlistDateIds.filter(
            (id) => id !== this.waitlistModalDateId
          )
        }
      } catch {
        this.attendanceLocked = false
        this.showErrorModal = true
      }
      this.showWaitlistModal = false
    },
    setAttendance(id, value) {
      const current = this.currentAttendance.find((date) => date.dateId === id)
      this.datesToUpdate = [
        ...this.datesToUpdate.filter((date) => date.dateId !== id),
        {
          ...(current ? current : { dateId: id }),
          attendance: value
        }
      ].sort((a, b) => a.dateId - b.dateId)
    },
    setValue(date) {
      const dateFound = this.datesToUpdate.find((d) => date.id === d.dateId)
      return dateFound ? dateFound.attendance : undefined
    },
    async goToPayment(paymentType) {
      this.$store.commit(PAYMENT_SET_TYPE, paymentType)
      if (paymentType === CREDIT_PAYMENT) {
        const hasEnoughCredits =
          this.isUnlimited ||
          parseInt(this.ownedCreditcardForThisEvent.remaining) >=
            this.creditsToPay
        if (!hasEnoughCredits) {
          this.showModalCreditsDone = true
        } else {
          const response = await this.$store.dispatch(EVENT_UPDATE_ATTENDANCE, {
            eventId: this.eventDate.eventId,
            dateId: this.dateId,
            securityHash: this.securityHash,
            creditcardId: this.ownedCreditcardForThisEvent.creditcard_id,
            dates: this.changedDatesPayload
          })
          if (response && response.status === 'success') {
            analytics.capture('event_attendance_saved', {
              event_id: this.eventDate.eventId
            })
            this.$router.push(
              `/events/${this.dateId}/${this.securityHash}?action=credits_used`
            )
          } else if (response && response.redirectUrl) {
            window.location.href = response.redirectUrl
          } else {
            this.showAttendanceError(response)
          }
        }
      } else if (paymentType === REFUND) {
        const response = await this.$store.dispatch(EVENT_UPDATE_ATTENDANCE, {
          eventId: this.eventDate.eventId,
          dateId: this.dateId,
          securityHash: this.securityHash,
          dates: this.changedDatesPayload
        })
        if (response && response.status === 'success') {
          this.$router.push(
            `/events/${this.dateId}/${this.securityHash}?action=refund`
          )
        } else if (response && response.redirectUrl) {
          window.location.href = response.redirectUrl
        } else {
          this.showAttendanceError(response)
        }
      } else {
        this.$router.push(
          `/events/${this.dateId}/signup/${this.securityHash}/payment`
        )
      }
    },
    showAttendanceError(response) {
      this.attendanceLocked = response?.code === ATTENDANCE_LOCKED_EVENT_STARTED
      this.showErrorModal = true
    },
    async saveAttendance() {
      if (this.amountToPay > 0) {
        this.goToPayment(
          this.ownedCreditcardForThisEvent ? CREDIT_PAYMENT : SINGLE_PAYMENT
        )
      } else if (this.amountToPay < 0) {
        this.goToPayment(REFUND)
      } else {
        const response = await this.$store.dispatch(EVENT_UPDATE_ATTENDANCE, {
          eventId: this.eventDate.eventId,
          dateId: this.dateId,
          securityHash: this.securityHash,
          dates: this.changedDatesPayload
        })
        if (response && response.status === 'success') {
          analytics.capture('event_attendance_saved', {
            event_id: this.eventDate.eventId
          })
          this.showConfirmModal = true
        } else {
          this.showAttendanceError(response)
        }
      }
    }
  },
  metaInfo() {
    return {
      title: `Inviplay`
    }
  }
}
</script>
